import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import MissionsPage from './pages/MissionsPage';
import MissionDetailPage from './pages/MissionDetailPage';
import NewMissionPage from './pages/NewMissionPage';
import IndicatorsPage from './pages/IndicatorsPage';
import UsersPage from './pages/UsersPage';
import SuppliersPage from './pages/SuppliersPage';
import CustomersPage from './pages/CustomersPage';
import CarriersPage from './pages/CarriersPage';
import ReasonsPage from './pages/ReasonsPage';
import BillingPage from './pages/BillingPage';
import MonitoringPage from './pages/MonitoringPage';
import PlansPage from './pages/PlansPage';
import TenantsPage from './pages/TenantsPage';
import ApiDocumentationPage from './pages/ApiDocumentationPage';
import ApiLoginPage from './pages/ApiLoginPage';
import PrestadorLoginPage from './pages/PrestadorLoginPage';
import PrestadorPortal from './pages/PrestadorPortal';
import PrestadoresPage from './pages/PrestadoresPage';
import Layout from './components/Layout';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

// Tenant Context - para filtro global de tenant (apenas Master)
const TenantContext = createContext(null);

export const useAuth = () => useContext(AuthContext);
export const useTenant = () => useContext(TenantContext);

const TenantProvider = ({ children }) => {
  const [selectedTenant, setSelectedTenant] = useState('all'); // 'all' ou CNPJ específico
  const [tenants, setTenants] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [tenantsLoaded, setTenantsLoaded] = useState(false);

  // Carregar lista de tenants (apenas para Master)
  const loadTenants = useCallback(async (token) => {
    if (!token || tenantsLoaded) return;
    setLoadingTenants(true);
    try {
      const response = await axios.get(`${API}/tenants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenants(response.data);
      setTenantsLoaded(true);
    } catch (e) {
      // Não é master ou erro
      setTenants([]);
    } finally {
      setLoadingTenants(false);
    }
  }, [tenantsLoaded]);

  const value = {
    selectedTenant,
    setSelectedTenant,
    tenants,
    loadTenants,
    loadingTenants,
    // Helper para adicionar query param nas chamadas API
    getTenantParam: () => selectedTenant && selectedTenant !== 'all' ? `?tenant_cnpj=${selectedTenant}` : ''
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('pr_token'));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem('pr_token');
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          logout();
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token, logout]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('pr_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password, phone, tenant_cnpj) => {
    const response = await axios.post(`${API}/auth/register`, { 
      name, 
      email, 
      password, 
      phone, 
      role: 'operacional',
      tenant_cnpj: tenant_cnpj ? tenant_cnpj.replace(/\D/g, '') : null
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('pr_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  // Direct authentication set (for external login flows like API login)
  const setAuth = (accessToken, userData) => {
    localStorage.setItem('pr_token', accessToken);
    setToken(accessToken);
    setUser(userData);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    setAuth,
    isAuthenticated: !!user,
    hasRole: (roles) => user && roles.includes(user.role)
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, loading, hasRole, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If user has api_access role, only allow access to /documentacao-api
  if (user?.role === 'api_access') {
    if (location.pathname !== '/documentacao-api') {
      return <Navigate to="/documentacao-api" replace />;
    }
    return children;
  }

  // If user has prestador role, only allow access to /prestador
  if (user?.role === 'prestador') {
    if (location.pathname !== '/prestador') {
      return <Navigate to="/prestador" replace />;
    }
    return children;
  }

  if (allowedRoles && !hasRole(allowedRoles)) {
    toast.error('Acesso não autorizado');
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// API Helper with auth
export const api = {
  get: async (endpoint, token) => {
    const response = await axios.get(`${API}${endpoint}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  post: async (endpoint, data, token) => {
    const response = await axios.post(`${API}${endpoint}`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  put: async (endpoint, data, token) => {
    const response = await axios.put(`${API}${endpoint}`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  delete: async (endpoint, token) => {
    const response = await axios.delete(`${API}${endpoint}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  }
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <TenantProvider>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/login-api" element={<ApiLoginPage />} />
            <Route path="/login-prestador" element={<Navigate to="/login" replace />} />
            <Route path="/plans" element={<PlansPage />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Layout><DashboardPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/missions" element={
              <ProtectedRoute>
                <Layout><MissionsPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/missions/new" element={
              <ProtectedRoute allowedRoles={['mestre', 'adm', 'gestor_operacional', 'operacional']}>
                <Layout><NewMissionPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/missions/:id" element={
              <ProtectedRoute>
                <Layout><MissionDetailPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/indicators" element={
              <ProtectedRoute>
                <Layout><IndicatorsPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/users" element={
              <ProtectedRoute allowedRoles={['mestre', 'adm']}>
                <Layout><UsersPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/suppliers" element={
              <ProtectedRoute allowedRoles={['mestre', 'adm', 'financeiro']}>
                <Layout><SuppliersPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/customers" element={
              <ProtectedRoute allowedRoles={['mestre', 'adm', 'financeiro']}>
                <Layout><CustomersPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/carriers" element={
              <ProtectedRoute allowedRoles={['mestre', 'adm']}>
                <Layout><CarriersPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/reasons" element={
              <ProtectedRoute allowedRoles={['mestre', 'adm']}>
                <Layout><ReasonsPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/billing" element={
              <ProtectedRoute allowedRoles={['mestre', 'adm', 'financeiro']}>
                <Layout><BillingPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/monitoring" element={
              <ProtectedRoute allowedRoles={['mestre']}>
                <Layout><MonitoringPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/tenants" element={
              <ProtectedRoute allowedRoles={['mestre']}>
                <Layout><TenantsPage /></Layout>
              </ProtectedRoute>
            } />
            
            
            <Route path="/documentacao-api" element={
              <ProtectedRoute allowedRoles={['mestre', 'api_access']}>
                <Layout><ApiDocumentationPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/prestadores" element={
              <ProtectedRoute allowedRoles={['mestre', 'adm', 'gestor_operacional']}>
                <Layout><PrestadoresPage /></Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/prestador" element={
              <ProtectedRoute allowedRoles={['prestador']}>
                <PrestadorPortal />
              </ProtectedRoute>
            } />
          </Routes>
        </TenantProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
